import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { Progress } from "../../components/progress/Progress";
import Button, {BtnProps} from "../../components/button/Button";
import { ConstMessage, PointTypeId, BonusSettingsCondition } from "../../config/const";
import { PtAwardedSettings, newPtAwardedSettings , PtBonusSettings, RangeStepsMap } from "../../model/PointData";
import { Controller, useForm } from "react-hook-form";
import { SelectFields, SelectItem } from "../../components/select/SelectFields";
import { RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { convertDateToSlashTypeZeroPadString } from "../../utility/ConvertUtily";
import TableList, { GridCol, GridRow } from "../../components/table/TableList"
import pointDataQuery from '../../firestore/PointData.query';
import { PointAwardedEditModal } from "./PointAwardedEditModal";
// ログイン中の団体管理者情報
import { auth } from "../../firebase";

import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import { ModalAndAlmightyDialog } from "../../components/modal/ModalAndAlmightyDialog";
import QRCode from "qrcode";
import { pdf } from "@react-pdf/renderer";
import { PointCodePDF } from "../../components/pdf/PointCodePDF";
import { TextField } from "../../components/fields/Fields";
import { DeleteConfirmationModal } from "../../components/modal/DeleteConfirmationModal";
import { checkOption } from "../../controller/OptionController";

export const PointAwardedInfo = () => {

  // ポイント付与項目設定：Id
  const { pointAwardedSettingsId } = useParams<{ pointAwardedSettingsId: string }>();

  const [isLoad, setIsLoad] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loadProgress, setLoadProgress] = useState<number>(-1);

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const [pointCategoryList, setPointCategoryList] = useState<SelectItem[]>([]);
  const [pointTypeList, setPointTypeList] = useState<SelectItem[]>([]);
  const [ptAwardedSettings, setPtAwardedSettings] = useState<PtAwardedSettings>(newPtAwardedSettings);
  const [ptBonusSettings, setPtBonusSettings] = useState<PtBonusSettings[]>([]);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [dialogChildren, setDialogChildren] = useState<React.ReactNode | undefined>();
  const [dialogActions, setDialogActions] = useState<BtnProps[]>([]);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [pdfButton, setPdfButton] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [rangeStepsData, setRangeStepsData] = useState<RangeStepsMap[]>([]);

  // ポイント種別
  const pointSubjectList = [
    { value: "0", name: "体重" },
    { value: "1", name: "血圧" },
    { value: "2", name: "体重と血圧" },
  ];
  
  // ポイント種別
  const pointKindList = [
    {value: "0", name: "通常"},
    {value: "1", name: "ボーナス"},
  ];
  // 付与対象
  const targetList = [
    {value: "0", name: "共通"},
    {value: "1", name: "男性"},
    {value: "2", name: "女性"},
  ];
  // ステータス
  const selectStatusItems = [
    {id: "disabled", title: "無効", value: "0" },
    {id: "enabled", title: "有効", value: "1" }
  ];
  // 設定有無共通
  const selectFlgItems = [
    {id: "false", title: "設定なし", value: "false" },
    {id: "true", title: "設定あり", value: "true" }
  ];
  // 単位指定
  const periodList = [
    {value:"", name: ""},
    {value:"none", name: "なし（無期限）"},
    {value:"day", name: "日"},
    {value:"week", name: "週"},
    {value:"month", name: "月"},
    {value:"year", name: "年"},
    {value:"designation", name: "期間"},
  ];

  // ボーナスポイント一覧のヘッダ設定
  const bonusPointTableColumns: GridCol[] = [
    {
      field: "no",
      headerName: "No.",
      style: "w-1/12 bg-indigo-50",
    },
    {
      field: "bonusCondition",
      headerName: "ボーナス条件",
      style: "w-3/12 bg-indigo-50",
    },
    {
      field: "bonusPoint",
      headerName: "ポイント数",
      style: "w-1/12 bg-indigo-50",
    },
  ];

  /**
   * 条件の名称編集
   * @param ptBonusSettings
   * @returns
   */
  const bonusConditionName = (ptBonusSettings :PtBonusSettings) => {
    switch (ptBonusSettings.condition) {
      case 'seqDay'  :
        return BonusSettingsCondition.SEQ_DAY.replace('%1', `${ptBonusSettings.numOfDays}`);
      case 'seqWeek' :
        return BonusSettingsCondition.SEQ_WEEK.replace('%1', `${ptBonusSettings.numOfDays}`);
      case 'seqMonth' :
        return BonusSettingsCondition.SEQ_MONTH.replace('%1', `${ptBonusSettings.numOfDays}`);
      case 'seqCount' :
        return BonusSettingsCondition.SEQ_COUNT.replace('%1', `${ptBonusSettings.numOfTimes}`);
      case 'periodDay' :
        return BonusSettingsCondition.PERIOD_DAY.replace('%1', `${ptBonusSettings.numOfDays}`).replace('%2', `${ptBonusSettings.numOfTimes}`);
      case 'periodWeek' :
        return BonusSettingsCondition.PERIOD_WEEK.replace('%1', `${ptBonusSettings.numOfDays}`).replace('%2', `${ptBonusSettings.numOfTimes}`);
      case 'periodMonth' :
        return BonusSettingsCondition.PERIOD_MONTH.replace('%1', `${ptBonusSettings.numOfDays}`).replace('%2', `${ptBonusSettings.numOfTimes}`);
      case 'periodRange' :
        return BonusSettingsCondition.PERIOD_RANGE
          .replace('%1', `${convertDateToSlashTypeZeroPadString(ptBonusSettings.dateFrom as Date)}`)
          .replace('%2', `${convertDateToSlashTypeZeroPadString(ptBonusSettings.dateTo as Date)}`)
          .replace('%3', `${ptBonusSettings.numOfTimes}`);
      case 'sumCount' :
        return BonusSettingsCondition.SUM_COUNT.replace('%1', `${ptBonusSettings.numOfTimes}`);
      default :
        return "";
    }
  };
  const bonusPointTableRowList: GridRow[] = ptBonusSettings.map<GridRow>((item) => {
    return {
      rowData: {
        no: (<div className={"w-[3em] text-right"}>{item.no}</div>),
        bonusCondition: bonusConditionName(item),
        bonusPoint: (<div className={"w-[5rem] text-right"}>{item.bonusPoints}</div>),
      },
      style: "bg-indigo-50"
    }
  });

  const { control } = useForm<PtAwardedSettings>({
  });
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  /**
   * ポイント設定画面表示/クローズ時処理
   */
  const toggleEditDialog = () => {
    setIsOpenEditDialog(!isOpenEditDialog);
  };
  /**
   * 削除ボタン押下時処理
   */
  const onClickDelete = async () => {
    try{
      await pointDataQuery.deletePtAwardedSettings(currentUser?.tenantId!, pointAwardedSettingsId!);
      dispatch(alertOpen(AlertValue("削除しました。", "success")));
      navigate("/pointAwardedSettings");
    }catch(err){
      dispatch(alertOpen(AlertValue("削除に失敗しました。", "success")));
    }
  }

  useEffect(() => {
    const f = async () => {
      try {
        if(pointAwardedSettingsId){
          // ポイント付与設定を取得
          const pointAwardedSettings = await pointDataQuery.getPtAwardedSettingsForId(currentUser?.tenantId!, pointAwardedSettingsId);
          console.log(pointAwardedSettings);
          setPtAwardedSettings(pointAwardedSettings);
          // ボーナスポイントを取得
          if (pointAwardedSettings.bonusSettingFlg) {
            const pointBonusSettings = await pointDataQuery.getPtBonusSettings(currentUser?.tenantId!, pointAwardedSettingsId)
            setPtBonusSettings(pointBonusSettings);
          } else {
            setPtBonusSettings([]);
          }
          // ポイントカテゴリリスト作成
          let ptCategoryList = [{id: String(pointAwardedSettings.categoryId), name: pointAwardedSettings.categoryName}];
          setPointCategoryList(ptCategoryList);
          // ポイントタイプリスト作成
          let ptTypeList = [{id: pointAwardedSettings.typeId, name: pointAwardedSettings.typeName}];
          setPointTypeList(ptTypeList);
          // PDF出力ボタンの表示有無
          const pdfButton = pdfButtonFlg(pointAwardedSettings);
          setPdfButton(pdfButton);
          //歩数範囲取得
          if(pointAwardedSettings.typeId == PointTypeId.STEPS_RANGE){
            let rangeStepsMap = pointAwardedSettings.rangeStepsMap;
            let rangeStepsData = [];
            if(rangeStepsMap != undefined){
              const keys = Object.keys(rangeStepsMap);
              for(let i = 0; i < keys.length; i++){
                const key = Number(keys[i]);
                if(rangeStepsMap[i].id === key){
                  // Form用の文字列型項目にデータを設定する
                  rangeStepsMap[i]._strBasePoint = rangeStepsMap[i].basePoint ? rangeStepsMap[i].basePoint?.toString() : "";
                  rangeStepsMap[i]._strRangeStepsLower = rangeStepsMap[i].rangeStepsLower ? rangeStepsMap[i].rangeStepsLower?.toString() : "";
                  rangeStepsMap[i]._strRangeStepsUpper = rangeStepsMap[i].rangeStepsUpper ? rangeStepsMap[i].rangeStepsUpper?.toString() : "";
                  rangeStepsData.push(rangeStepsMap[key]);
                };
              };
              setRangeStepsData(rangeStepsData);
            }
          }
        }
      } catch (error) {
        console.error(error);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      }
      setIsLoad(false);
    };
    f();
  },[dispatch, isOpenEditDialog]);

  /**
   * ラジオボタン名称取得
   * @param selectItems ラジオボタンItem
   * @param value
   * @returns
   */
  const getRadioName = (selectItems : RadioButtonItem[] , value : any) => {
    return selectItems.filter(item => item.value == String(value)).at(0)?.title!
  }

  const pdfButtonFlg = (pointAwardedSettings: PtAwardedSettings) => {
    // ポイントタイプを判定
    const typeId = pointAwardedSettings.typeId;
    if(typeId === PointTypeId.VOLUNTEER_REGIST ||
       typeId === PointTypeId.VOLUNTEER_ACTIVITY ||
       typeId === PointTypeId.OTHER ||
       typeId === PointTypeId.GROUP_REGIST
    ){
      // ステータスが有効か判定
      const now = new Date();
      const nowDate = new Date(now.getFullYear(), now.getMonth(),now.getDate());
      const validPeriodDateFrom = pointAwardedSettings.validPeriodDateFrom ? 
        new Date(pointAwardedSettings.validPeriodDateFrom.getFullYear(), pointAwardedSettings.validPeriodDateFrom.getMonth(),pointAwardedSettings.validPeriodDateFrom.getDate()): "";
      const validPeriodDateTo = pointAwardedSettings.validPeriodDateTo ? 
        new Date(pointAwardedSettings.validPeriodDateTo.getFullYear(), pointAwardedSettings.validPeriodDateTo.getMonth(),pointAwardedSettings.validPeriodDateTo.getDate()): "";
      if(pointAwardedSettings.status === 1 && 
        pointAwardedSettings.validPeriodDateFrom && validPeriodDateFrom <= nowDate &&
        (validPeriodDateTo === "" || (pointAwardedSettings.validPeriodDateTo && validPeriodDateTo >= nowDate))
      ){
        return true;
      };
    };
    return false;
  };

  const toggleDialog = () => {
    setIsOpenDialog(isOpenDialog);
  };

  const toggleDeleteDialog = () => {
    setIsOpenDeleteDialog(!isOpenDeleteDialog);
  };

  const initDialog = () => {
    setDialogTitle("");
    setDialogChildren("");
    setDialogActions([]);
  };

  // PDF出力確認モーダルの設定
  const pdfDownloadConfirmation = () => {
    const actions: BtnProps[] = [
      {
        color: "indigo",
        outline: true,
        children: "キャンセル",
        className: "w-[132px]",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
        }
      },
      {
        color: "indigo",
        children: "ダウンロード",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
          pointCodePdfDownload();
        }
      },
    ];
    const dialogChildren = (
      <>
        二次元コードPDFをダウンロードしますか？
      </>
    );
    setIsOpenDialog(true);
    setDialogChildren(dialogChildren);
    setDialogActions(actions);
  };

  // PDF出力処理
  const pointCodePdfDownload = async () => {
    try {
      // QRコード画像データ生成
      const qrCodeData = await QRCode.toDataURL(`point_${ptAwardedSettings.id}`, { errorCorrectionLevel: 'M' });
      // PDFダウンロード
      const blob = await pdf(<PointCodePDF ptAwarded={ptAwardedSettings} qrCodeData={qrCodeData}/>).toBlob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `二次元コード_${ptAwardedSettings.name}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error(err);
      dispatch(
        alertOpen(AlertValue(ConstMessage.ERR_PDF_DOWNLOAD, "danger"))
      );
    }     
  };

  return (
    <>
      <div className="flex">
        <div className="relative z-0 flex flex-1">
          <main className="relative z-0 flex-1 focus:outline-none order-last">
            <nav
              className="flex items-start px-4 py-3"
              aria-label="Breadcrumb"
            >
              <div className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer">
                <span onClick={() => navigate("/pointAwardedSettings")} className="flex items-center">
                  <ChevronLeftIcon className="-ml-2 h-5 w-5 text-gray-400 mr-3" />一覧へ
                </span>
              </div>
            </nav>
            <Progress
              subText={ConstMessage.PROGRESS_SUB_TEXT}
              open={loadProgress >= 0 && loadProgress < 100}
              progressNum={loadProgress}
              color={"green"}
            />
            <div className="p-2">
              <div className="max-w-5xl px-3 sm:px-6 lg:px-8">
                <div className="flex min-w-0 flex-1 ">
                  <h2 className="text-xl font-bold text-gray-900">
                    ポイント設定詳細
                  </h2>
                </div>
              </div>
              <div className="px-2 pb-4 sm:p-2 ml-7">
                <div className=" max-w-[1295px]">
                  <div className="mt-3 bg-indigo-50">
                    <div className="overflow-y-auto">
                      <div className="px-2 text-left pb-8 max-h-[calc(100vh_-_17rem)] ">
                        <div className="px-2 pb-4 sm:p-4">
                          <div className="flex items-center gap-x-2 h-[5rem]">
                            {/* ポイントカテゴリ */}
                            <div className="mr-6">
                              <Controller
                                name={"categoryId"}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <div className="flex flex-col gap-y-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {"ポイントカテゴリ"}
                                    </label>
                                    <SelectFields
                                      {...field}
                                      label=""
                                      color="indigo"
                                      className="h-[3.5rem]"
                                      selectClassName="px-3 py-2 h-auto bg-indigo-50"
                                      style={{ 'width': '13rem', 'border-width': 0, 'background-image': 'none' }}
                                      value={ptAwardedSettings.categoryId}
                                      selectItems={pointCategoryList}
                                      disabled
                                    />
                                  </div>
                                )}
                              />
                            </div>
                            {/* ポイントタイプ */}
                            <div className="mr-6">
                              <Controller
                                name={"typeId"}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <div className="flex flex-col gap-y-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {"ポイントタイプ"}
                                    </label>
                                    <SelectFields
                                      {...field}
                                      label=""
                                      color="indigo"
                                      className="h-[3.5rem]"
                                      selectClassName="px-3 py-2 h-auto bg-indigo-50"
                                      value={ptAwardedSettings.typeId}
                                      style={{ 'width': '13rem', 'border-width': 0, 'background-image': 'none' }}
                                      selectItems={pointTypeList}
                                      disabled
                                    />
                                  </div>
                                )}
                              />
                            </div>
                            {ptAwardedSettings.typeId == "" ? (
                              // ポイントタイプ未選択
                              <></>
                            ) : (
                              <>
                                {/* ポイント名 */}
                                <div className="flex flex-col">
                                  <h3 className="text-sm text-gray-700 mb-1">
                                    {"ポイント名"}
                                  </h3>
                                  <Controller
                                    name={"name"}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                      <TextField
                                        {...field}
                                        type={"text"}
                                        label={""}
                                        style={{ 'width': '37rem', 'border-width': 0 }}
                                        className={"h-[3.5rem]"}
                                        value={ptAwardedSettings.name}
                                        inputClassName={"bg-indigo-50"}
                                        disabled
                                      />
                                    )}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                          {ptAwardedSettings.typeId == "" ? (
                            // ポイントタイプ未選択
                            <></>
                          ) : (
                            <>
                              <div className="min-w-[1180px]">
                                <div className="flex gap-x-[2rem] mt-2">
                                  {ptAwardedSettings.typeId == PointTypeId.STEPS_RANGE ? (
                                    <div className={`text-left sm:grid sm:items-start sm:gap-4 sm:grid-cols-4`}>
                                      <div className="flex flex-col gap-y-1">
                                        <h3 className="text-sm text-gray-700">
                                          {"ポイント種別"}
                                        </h3>
                                        <Controller
                                          control={control}
                                          name={"pointKind"}
                                          render={({ field, fieldState }) => (
                                            <SelectFields
                                              {...field}
                                              label=""
                                              color="indigo"
                                              className="w-[8.2rem]"
                                              selectClassName="px-3 py-2 h-auto bg-indigo-50"
                                              value={ptAwardedSettings.pointKind}
                                              selectItems={pointKindList}
                                              style={{ 'width': '7.5rem', 'border-width': 0, 'background-image': 'none' }}
                                              disabled
                                            />
                                          )}
                                        />
                                      </div>
                                      <Controller
                                        name={"target"}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                          <div className="flex flex-col gap-y-1">
                                            <h3 className="text-sm text-gray-700">
                                              {"付与対象"}
                                            </h3>
                                            <SelectFields
                                              {...field}
                                              label=""
                                              color="indigo"
                                              className="w-[8.2rem]"
                                              selectClassName="px-3 py-2 h-auto bg-indigo-50"
                                              selectItems={targetList}
                                              value={ptAwardedSettings.target}
                                              style={{ 'width': '7.5rem', 'border-width': 0, 'background-image': 'none' }}
                                              disabled
                                            />
                                          </div>
                                        )}
                                      />
                                    </div>
                                  ) : (
                                    <>{(ptAwardedSettings.typeId == PointTypeId.STEPS_SPECIFIED
                                      || ptAwardedSettings.typeId == PointTypeId.STEPS_TARGET) ? (
                                      <div className={`text-left sm:grid sm:items-start sm:gap-4 sm:grid-cols-4`}>
                                        <Controller
                                          name={"basePoint"}
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <div className="w-[8.5rem]">
                                              <h3 className="text-sm text-gray-700">
                                                {"ポイント数"}
                                              </h3>
                                              <TextField
                                                {...field}
                                                type={"number"}
                                                label={""}
                                                color={"indigo"}
                                                className={"w-[8.2rem]"}
                                                inputClassName={"bg-indigo-50"}
                                                style={{ 'text-align': 'right', 'width': '7.5rem', 'border-width': 0 }}
                                                disabled
                                                value={ptAwardedSettings.pointKind === 0 ? ptAwardedSettings.basePoint : ""}
                                              />
                                            </div>
                                          )}
                                        />
                                        <Controller
                                          control={control}
                                          name={"pointKind"}
                                          render={({ field, fieldState }) => (
                                            <div className="flex flex-col gap-y-1">
                                              <h3 className="text-sm text-gray-700">
                                                {"ポイント種別"}
                                              </h3>
                                              <SelectFields
                                                {...field}
                                                label=""
                                                color="indigo"
                                                className="w-[8.2rem]"
                                                selectClassName="px-3 py-2 h-auto bg-indigo-50"
                                                value={ptAwardedSettings.pointKind}
                                                selectItems={pointKindList}
                                                style={{ 'width': '7.5rem', 'border-width': 0, 'background-image': 'none' }}
                                                disabled
                                              />
                                            </div>
                                          )}
                                        />
                                        <Controller
                                          name={"target"}
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <div className="flex flex-col gap-y-1">
                                              <h3 className="text-sm text-gray-700">
                                                {"付与対象"}
                                              </h3>
                                              <SelectFields
                                                {...field}
                                                label=""
                                                color="indigo"
                                                className="w-[8.2rem]"
                                                selectClassName="px-3 py-2 h-auto bg-indigo-50"
                                                selectItems={targetList}
                                                value={ptAwardedSettings.target}
                                                style={{ 'width': '7.5rem', 'border-width': 0, 'background-image': 'none' }}
                                                disabled
                                              />
                                            </div>
                                          )}
                                        />
                                        <Controller
                                          name={"steps"}
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <div className="w-[8.5rem]">
                                              <h3 className="text-sm text-gray-700">
                                                {ptAwardedSettings.typeId == PointTypeId.STEPS_SPECIFIED ? "指定歩数" : "目標歩数/日"}
                                              </h3>
                                              <TextField
                                                {...field}
                                                type={"number"}
                                                label={""}
                                                color={"indigo"}
                                                className={"w-[8.2rem]"}
                                                style={{ 'text-align': 'right', 'width': '7.5rem', 'border-width': 0 }}
                                                inputClassName={"bg-indigo-50"}
                                                value={ptAwardedSettings.steps}
                                                disabled
                                              />
                                            </div>
                                          )}
                                        />
                                      </div>
                                    ) : (
                                      <>{ptAwardedSettings.typeId == PointTypeId.VITAL_REGIST ? (
                                          <div className="flex flex-wrap gap-x-8 items-start">
                                            <div className="flex flex-col gap-y-1 w-[8.5rem]">
                                              {/* ポイント対象 */}
                                              <h3 className="text-sm text-gray-700">ポイント対象</h3>
                                              <Controller
                                                name="pointSubject"
                                                control={control}
                                                render={({ field, fieldState }) => (
                                                  <>
                                                    <SelectFields
                                                      {...field}
                                                      label=""
                                                      color="indigo"
                                                      className="w-full"
                                                      selectClassName="px-3 py-2 h-auto bg-indigo-50"
                                                      selectItems={pointSubjectList}
                                                      value={ptAwardedSettings.pointSubject}
                                                      style={{ 'border-width': 0, 'background-image': 'none' }}
                                                      disabled
                                                    />
                                                  </>
                                                )}
                                              />
                                            </div>
                                            {/* 体重 */}
                                            {(ptAwardedSettings.pointSubject) !== 1 && (
                                              <div className="flex flex-col gap-y-1 w-[calc(25%-1.5rem)] min-w-[8.5rem]">
                                                <Controller
                                                  name="registWtPoint"
                                                  control={control}
                                                  render={({ field, fieldState }) => (
                                                    <div className="">
                                                      <h3 className="text-sm text-gray-700 whitespace-nowrap">
                                                        {(ptAwardedSettings.pointSubject) === 2 ? 'ポイント数（体重登録）' : 'ポイント数 '}
                                                      </h3>
                                                      <TextField
                                                        {...field}
                                                        type={"number"}
                                                        label={""}
                                                        color={"indigo"}
                                                        className="w-[8.5rem]"
                                                        style={{ 'text-align': 'right', 'border-width': 0 }}
                                                        inputClassName={"bg-indigo-50"}
                                                        value={ptAwardedSettings.registWtPoint !== undefined ? ptAwardedSettings.registWtPoint : ""}
                                                        disabled
                                                      />
                                                    </div>
                                                  )}
                                                />
                                              </div>
                                            )}
                                            {/* 血圧 */}
                                            {(ptAwardedSettings.pointSubject) !== 0 && (
                                              <div className="flex flex-col gap-y-1 w-[calc(25%-1.5rem)] min-w-[8.5rem]">
                                                <Controller
                                                  name="_strRegistBpPoint"
                                                  control={control}
                                                  render={({ field, fieldState }) => (
                                                    <>
                                                      <h3 className="text-sm text-gray-700 whitespace-nowrap">
                                                        {(ptAwardedSettings.pointSubject) === 2 ? 'ポイント数（血圧登録）' : 'ポイント数 '}
                                                      </h3>
                                                      <TextField
                                                        {...field}
                                                        type={"number"}
                                                        label={""}
                                                        color={"indigo"}
                                                        className="w-[8.5rem]"
                                                        style={{ 'text-align': 'right', 'border-width': 0 }}
                                                        inputClassName={"bg-indigo-50"}
                                                        value={ptAwardedSettings.registBpPoint !== undefined ? ptAwardedSettings.registBpPoint : ""}
                                                        disabled
                                                      />
                                                    </>
                                                  )}
                                                />
                                              </div>
                                            )}
                                            {/* 同時登録 */}
                                            {(ptAwardedSettings.pointSubject) === 2 && (
                                              <div className="flex flex-col gap-y-1 w-[calc(25%-1.5rem)] min-w-[8.5rem]">
                                                <Controller
                                                  name="_strRegistSamePoint"
                                                  control={control}
                                                  render={({ field, fieldState }) => (
                                                    <div className="">
                                                      <h3 className="text-sm text-gray-700">ポイント数（同時登録）</h3>
                                                      <TextField
                                                        {...field}
                                                        type={"number"}
                                                        label={""}
                                                        color={"indigo"}
                                                        className="w-[8.5rem]"
                                                        style={{ 'text-align': 'right', 'border-width': 0 }}
                                                        inputClassName={"bg-indigo-50"}
                                                        value={ptAwardedSettings.registSamePoint !== undefined ? ptAwardedSettings.registSamePoint : ""}
                                                        disabled
                                                      />
                                                    </div>
                                                  )}
                                                />
                                              </div>
                                            )}
                                          </div>
                                      ) : (
                                        <>{ptAwardedSettings.typeId == PointTypeId.VOLUNTEER_ACTIVITY ? (
                                          <div className={`text-left sm:grid sm:items-start sm:gap-4 sm:grid-cols-4`}>
                                            <Controller
                                              name={"basePoint"}
                                              control={control}
                                              render={({ field, fieldState }) => (
                                                <div className="w-[8.5rem]">
                                                  <h3 className="text-sm text-gray-700">
                                                    {"ポイント数"}
                                                  </h3>
                                                  <TextField
                                                    {...field}
                                                    type={"number"}
                                                    label={""}
                                                    color={"indigo"}
                                                    className={"w-[8.2rem]"}
                                                    style={{ 'text-align': 'right', 'width': '7.5rem', 'border-width': 0 }}
                                                    inputClassName={"bg-indigo-50"}
                                                    value={ptAwardedSettings.basePoint}
                                                    disabled
                                                  />
                                                </div>
                                              )}
                                            />
                                            <Controller
                                              name={"activityTime"}
                                              control={control}
                                              render={({ field, fieldState }) => (
                                                <div className="w-[8.5rem]">
                                                  <h3 className="text-sm text-gray-700">
                                                    {"活動時間（分）"}
                                                  </h3>
                                                  <TextField
                                                    {...field}
                                                    type={"number"}
                                                    label={""}
                                                    color={"indigo"}
                                                    className={"w-[8.2rem]"}
                                                    style={{ 'text-align': 'right', 'width': '5rem', 'border-width': 0 }}
                                                    inputClassName={"bg-indigo-50"}
                                                    value={ptAwardedSettings.activityTime}
                                                    disabled
                                                  />
                                                </div>
                                              )}
                                            />
                                          </div>
                                        ) : (
                                          <div className={`text-left sm:grid sm:items-start sm:gap-4 sm:grid-cols-4`}>
                                            <Controller
                                              name={"basePoint"}
                                              control={control}
                                              render={({ field, fieldState }) => (
                                                <div className="w-[8.5rem]">
                                                  <h3 className="text-sm text-gray-700">
                                                    {"ポイント数"}
                                                  </h3>
                                                  <TextField
                                                    {...field}
                                                    type={"number"}
                                                    label={""}
                                                    color={"indigo"}
                                                    className={"w-[8.2rem]"}
                                                    style={{ 'text-align': 'right', 'width': '7.5rem', 'border-width': 0 }}
                                                    inputClassName={"bg-indigo-50"}
                                                    value={ptAwardedSettings.basePoint}
                                                    disabled
                                                  />
                                                </div>
                                              )}
                                            />
                                          </div>
                                        )}</>
                                      )}</>
                                    )}</>
                                  )}
                                </div>
                                {/* ステータス */}
                                <div className="relative mt-10 shadow ring-1 ring-black ring-opacity-5 min-w-[37rem] max-w-[37rem]">
                                  <div className="flex">
                                    <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                      <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                        ステータス
                                      </label>
                                    </span>
                                  </div>
                                  <div className="pl-5 pt-2 pb-5 pr-5 mt-4 text-sm">
                                    {getRadioName(selectStatusItems, ptAwardedSettings.status)}
                                    <div className="ml-7 relative mt-5 shadow ring-1 ring-black ring-opacity-5 w-[32.5rem]">
                                      <div className="flex">
                                        <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                          <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                            有効期間
                                          </label>
                                        </span>
                                      </div>
                                      <div className="flex mr-2 gap-x-3 ml-2">
                                        <div className="h-full pt-5 pb-5">
                                          <Controller
                                            name={"validPeriodDateFrom"}
                                            control={control}
                                            render={({ field, fieldState }) => (
                                              <div className="flex items-center">
                                                <h3 className="text-sm text-gray-700 text-end">
                                                  {"開始年月日"}
                                                </h3>
                                                <TextField
                                                  {...field}
                                                  type={ptAwardedSettings.validPeriodDateFrom ? "date" : "label"}
                                                  label={""}
                                                  color={"indigo"}
                                                  className={"flex items-center gap-x-3 h-10"}
                                                  selectClassName={"py-2 h-auto"}
                                                  style={{ 'width': '8.5rem', 'border-width': 0 }}
                                                  inputClassName={"bg-indigo-50 mb-1"}
                                                  value={ptAwardedSettings.validPeriodDateFrom ? ptAwardedSettings.validPeriodDateFrom : ""}
                                                  disabled
                                                />
                                              </div>
                                            )}
                                          />
                                        </div>
                                        <div className="h-full pt-5 pb-5">
                                          <Controller
                                            name={"validPeriodDateTo"}
                                            control={control}
                                            render={({ field, fieldState }) => (
                                              <div className="flex items-center">
                                                <h3 className="text-sm text-gray-700 text-end">
                                                  {"終了年月日"}
                                                </h3>
                                                <TextField
                                                  {...field}
                                                  type={ptAwardedSettings.validPeriodDateTo ? "date" : "label"}
                                                  label={""}
                                                  color={"indigo"}
                                                  className={"flex items-center gap-x-3 h-10"}
                                                  selectClassName={"py-2 h-auto"}
                                                  style={{ 'width': '8.5rem', 'border-width': 0 }}
                                                  inputClassName={"bg-indigo-50 mb-1"}
                                                  value={ptAwardedSettings.validPeriodDateTo ? ptAwardedSettings.validPeriodDateTo : ""}
                                                  disabled
                                                />
                                              </div>
                                            )}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {ptAwardedSettings.typeId == PointTypeId.STEPS_RANGE ? (
                                  // 歩数範囲毎
                                  <>
                                    <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 min-w-[47rem] max-w-[47rem]">
                                      <div className="flex">
                                        <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                          <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                            歩数範囲
                                          </label>
                                        </span>
                                      </div>
                                      <div className="pl-5 pt-2 pb-5 pr-5 mt-4">
                                        {rangeStepsData.map((item:any) => (
                                          <div className="flex pt-2 ml-7">
                                            <Controller
                                              name={"rangeStepsLower"}
                                              control={control}
                                              render={({ field, fieldState }) => (
                                                <div className="flex items-center">
                                                  <h3 className="text-sm text-gray-700 text-end w-[5rem]">
                                                    {`範囲 ${item.id +1}`}
                                                  </h3>
                                                  <TextField
                                                    {...field}
                                                    type={"number"}
                                                    label={""}
                                                    color={"indigo"}
                                                    className={"flex items-center gap-x-3 h-10"}
                                                    value={item.rangeStepsLower}
                                                    selectClassName={"py-2 h-auto"}
                                                    style={{ 'width': '8.5rem', 'text-align': 'right', 'border-width': 0 }}
                                                    inputClassName={"bg-indigo-50 mb-1"}
                                                    disabled
                                                  />
                                                  <span>　～　</span>
                                                </div>
                                              )}
                                            />
                                            <Controller
                                              name={"rangeStepsUpper"}
                                              control={control}
                                              render={({ field, fieldState }) => (
                                                <div className="flex items-center">
                                                  <TextField
                                                    {...field}
                                                    type={"number"}
                                                    label={""}
                                                    color={"indigo"}
                                                    className={"flex items-center h-10"}
                                                    selectClassName={"py-2 h-auto"}
                                                    style={{ 'width': '8.5rem', 'text-align': 'right', 'border-width': 0 }}
                                                    inputClassName={"bg-indigo-50 mb-1"}
                                                    value={item._strRangeStepsUpper}
                                                    disabled
                                                  />
                                                </div>
                                              )}
                                            />
                                            <Controller
                                              name={"basePoint"}
                                              control={control}
                                              render={({ field, fieldState }) => (
                                                <div className="flex items-center ml-8">
                                                  <h3 className="text-sm text-gray-700">
                                                    {"ポイント数"}
                                                  </h3>
                                                  <TextField
                                                    {...field}
                                                    type={"number"}
                                                    label={""}
                                                    color={"indigo"}
                                                    className={"flex items-center gap-x-3 h-10"}
                                                    inputClassName={"bg-indigo-50"}
                                                    style={{ 'text-align': 'right', 'width': '7rem', 'border-width': 0 }}
                                                    disabled
                                                    value={item.basePoint}
                                                  />
                                                </div>
                                              )}
                                            />
                                          </div>
                                        ))}
                                        <div className="flex items-center mt-5 ml-7">
                                          <Controller
                                            name={"rangeStepsUnit"}
                                            control={control}
                                            render={({ field, fieldState }) => (
                                              <div className="flex items-center">
                                                <h3 className="text-sm text-gray-700 text-end">
                                                  {"単位指定"}
                                                </h3>
                                                <SelectFields
                                                  {...field}
                                                  color="indigo"
                                                  className="h-10 flex items-center gap-x-3"
                                                  label={""}
                                                  selectClassName="px-3 py-2 h-auto bg-indigo-50 mb-1"
                                                  style={{ 'width': '10rem', 'border-width': 0, 'background-image': 'none' }}
                                                  value={ptAwardedSettings.rangeStepsUnit}
                                                  selectItems={periodList}
                                                  disabled
                                                />
                                              </div>
                                            )}
                                          />
                                        </div>
                                        <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 ml-7 w-[32.5rem]">
                                          <div className="flex">
                                            <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                              <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                                指定期間
                                              </label>
                                            </span>
                                          </div>
                                          <div className="flex mr-2 gap-x-3 ml-2">
                                            <div className="h-full pt-5 pb-5">
                                              <Controller
                                                name={"rangeStepsPeriodDateFrom"}
                                                control={control}
                                                render={({ field, fieldState }) => (
                                                  <div className="flex items-center">
                                                    <h3 className="text-sm text-gray-700 text-end">
                                                      {"開始年月日"}
                                                    </h3>
                                                    <TextField
                                                      {...field}
                                                      type={ptAwardedSettings.rangeStepsPeriodDateFrom ? "date" : "label"}
                                                      label={""}
                                                      color={"indigo"}
                                                      className={"flex items-center gap-x-3 h-10"}
                                                      selectClassName={"py-2 h-auto"}
                                                      style={{ 'width': '8.5rem', 'border-width': 0 }}
                                                      inputClassName={"bg-indigo-50 mb-1"}
                                                      value={ptAwardedSettings.rangeStepsPeriodDateFrom ? ptAwardedSettings.rangeStepsPeriodDateFrom : ""}
                                                      disabled
                                                    />
                                                  </div>
                                                )}
                                              />
                                            </div>
                                            <div className="h-full pt-5 pb-5">
                                              <Controller
                                                name={"rangeStepsPeriodDateTo"}
                                                control={control}
                                                render={({ field, fieldState }) => (
                                                  <div className="flex items-center">
                                                    <h3 className="text-sm text-gray-700 text-end">
                                                      {"終了年月日"}
                                                    </h3>
                                                    <TextField
                                                      {...field}
                                                      type={ptAwardedSettings.rangeStepsPeriodDateTo ? "date" : "label"}
                                                      label={""}
                                                      color={"indigo"}
                                                      className={"flex items-center gap-x-3 h-10"}
                                                      selectClassName={"py-2 h-auto"}
                                                      style={{ 'width': '8.5rem', 'border-width': 0 }}
                                                      inputClassName={"bg-indigo-50 mb-1"}
                                                      value={ptAwardedSettings.rangeStepsPeriodDateTo ? ptAwardedSettings.rangeStepsPeriodDateTo : ""}
                                                      disabled
                                                    />
                                                  </div>
                                                )}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <></>
                                )}
                                {ptAwardedSettings.typeId == PointTypeId.STEPS_RANGE ? (
                                  <></>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-x-[2rem]">
                                      {/* 付与回数制限 */}
                                      <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 min-w-[37rem] max-w-[37rem]">
                                        <div className="flex">
                                          <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                            <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                              付与回数制限
                                            </label>
                                          </span>
                                        </div>
                                        <div className="pl-5 pt-2 pb-5 pr-5 mt-4 text-sm">
                                          {getRadioName(selectFlgItems, ptAwardedSettings.numberLimitFlg)}
                                          <div className="ml-7">
                                            <Controller
                                              name={"numberLimitMap.numberLimit"}
                                              control={control}
                                              render={({ field, fieldState }) => (
                                                <div className="flex items-center mt-5">
                                                  <h3 className="text-sm text-gray-700 text-end">
                                                    {"制限回数"}
                                                  </h3>
                                                  <TextField
                                                    {...field}
                                                    type={"number"}
                                                    label={""}
                                                    color={"indigo"}
                                                    className={"flex items-center gap-x-3 h-10"}
                                                    selectClassName={"py-2 h-auto"}
                                                    style={{ 'width': '5rem', 'text-align': 'right', 'border-width': 0 }}
                                                    value={ptAwardedSettings.numberLimitMap ? ptAwardedSettings.numberLimitMap!.numberLimit : ""}
                                                    inputClassName={"bg-indigo-50 mb-1"}
                                                    disabled
                                                  />
                                                </div>
                                              )}
                                            />
                                            <div className="flex items-center mt-5">
                                              <Controller
                                                name={"numberLimitMap.period"}
                                                control={control}
                                                render={({ field, fieldState }) => (
                                                  <div className="flex items-center">
                                                    <h3 className="text-sm text-gray-700 text-end">
                                                      {"単位指定"}
                                                    </h3>
                                                    <SelectFields
                                                      {...field}
                                                      color="indigo"
                                                      className="h-10 flex items-center gap-x-3"
                                                      label={""}
                                                      selectClassName={"bg-indigo-50 mb-1 px-3 py-2 h-auto"}
                                                      style={{ 'width': '10rem', 'border-width': 0, 'background-image': 'none' }}
                                                      value={ptAwardedSettings.numberLimitMap ? ptAwardedSettings.numberLimitMap.period : ""}
                                                      selectItems={periodList}
                                                      disabled
                                                    />
                                                  </div>
                                                )}
                                              />
                                            </div>
                                          </div>
                                          <div className="ml-7 relative mt-5 shadow ring-1 ring-black ring-opacity-5  w-[32.5rem]">
                                            <div className="flex">
                                              <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                                <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                                  指定期間
                                                </label>
                                              </span>
                                            </div>
                                            <div className="flex mr-2 gap-x-3 ml-2">
                                              <div className="h-full pt-5 pb-5">
                                                <Controller
                                                  name={"numberLimitMap.dateFrom"}
                                                  control={control}
                                                  render={({ field, fieldState }) => (
                                                    <div className="flex items-center">
                                                      <h3 className="text-sm text-gray-700 text-end">
                                                        {"開始年月日"}
                                                      </h3>
                                                      <TextField
                                                        {...field}
                                                        type={(ptAwardedSettings.numberLimitMap && ptAwardedSettings.numberLimitMap.dateFrom) ? "date" : "label"}
                                                        label={""}
                                                        color={"indigo"}
                                                        className={"flex items-center gap-x-3 h-10"}
                                                        selectClassName={"py-2 h-auto"}
                                                        style={{ 'width': '8.5rem', 'border-width': 0 }}
                                                        inputClassName={"bg-indigo-50 mb-1"}
                                                        value={(ptAwardedSettings.numberLimitMap && ptAwardedSettings.numberLimitMap.dateFrom) ? ptAwardedSettings.numberLimitMap!.dateFrom : ""}
                                                        disabled

                                                      />
                                                    </div>
                                                  )}
                                                />
                                              </div>
                                              <div className="h-full pt-5 pb-5">
                                                <Controller
                                                  name={"numberLimitMap.dateTo"}
                                                  control={control}
                                                  render={({ field, fieldState }) => (
                                                    <div className="flex items-center">
                                                      <h3 className="text-sm text-gray-700 text-end">
                                                        {"終了年月日"}
                                                      </h3>
                                                      <TextField
                                                        {...field}
                                                        type={(ptAwardedSettings.numberLimitMap && ptAwardedSettings.numberLimitMap.dateTo) ? "date" : "label"}
                                                        label={""}
                                                        color={"indigo"}
                                                        className={"flex items-center gap-x-3 h-10"}
                                                        selectClassName={"py-2 h-auto"}
                                                        style={{ 'width': '8.5rem', 'border-width': 0 }}
                                                        inputClassName={"bg-indigo-50 mb-1"}
                                                        value={(ptAwardedSettings.numberLimitMap && ptAwardedSettings.numberLimitMap.dateTo) ? ptAwardedSettings.numberLimitMap.dateTo : ""}
                                                        disabled
                                                      />
                                                    </div>
                                                  )}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      {/* ポイント数合計上限 */}
                                      <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 min-w-[37rem] max-w-[37rem]">
                                        <div className="flex">
                                          <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                            <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                              ポイント数合計上限
                                            </label>
                                          </span>
                                        </div>
                                        <div className="pl-5 pt-2 pb-5 pr-5 mt-4 text-sm">
                                          {getRadioName(selectFlgItems, ptAwardedSettings.pointLimitFlg)}
                                          <div className="ml-7">
                                            <Controller
                                              name={"pointLimitMap.pointLimit"}
                                              control={control}
                                              render={({ field, fieldState }) => (
                                                <div className="flex items-center mt-5">
                                                  <h3 className="text-sm text-gray-700 text-end">
                                                    {"ポイント上限"}
                                                  </h3>
                                                  <TextField
                                                    {...field}
                                                    type={"number"}
                                                    label={""}
                                                    color={"indigo"}
                                                    className={"flex items-center gap-x-3 h-10"}
                                                    selectClassName={"py-2 h-auto"}
                                                    style={{ 'width': '5rem', 'text-align': 'right', 'border-width': 0 }}
                                                    inputClassName={"bg-indigo-50 mb-1"}
                                                    value={ptAwardedSettings.pointLimitMap ? ptAwardedSettings.pointLimitMap!.pointLimit : ""}
                                                    disabled
                                                  />
                                                </div>
                                              )}
                                            />
                                            <div className="flex items-center mt-5 ml-7">
                                              <Controller
                                                name={"pointLimitMap.period"}
                                                control={control}
                                                render={({ field, fieldState }) => (
                                                  <div className="flex items-center">
                                                    <h3 className="text-sm text-gray-700 text-end">
                                                      {"単位指定"}
                                                    </h3>
                                                    <SelectFields
                                                      {...field}
                                                      color="indigo"
                                                      className="h-10 flex items-center gap-x-3"
                                                      label={""}
                                                      selectClassName={"bg-indigo-50 mb-1 px-3 py-2 h-auto"}
                                                      style={{ 'width': '10rem', 'border-width': 0, 'background-image': 'none' }}
                                                      selectItems={periodList}
                                                      value={ptAwardedSettings.pointLimitMap ? ptAwardedSettings.pointLimitMap.period : ""}
                                                      disabled
                                                    />
                                                  </div>
                                                )}
                                              />
                                            </div>
                                          </div>
                                          <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 ml-7 w-[32.5rem]" >
                                            <div className="flex">
                                              <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                                <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                                  指定期間
                                                </label>
                                              </span>
                                            </div>
                                            <div className="flex mr-2 gap-x-3 ml-2">
                                              <div className="h-full pt-5 pb-5">
                                                <Controller
                                                  name={"pointLimitMap.dateFrom"}
                                                  control={control}
                                                  render={({ field, fieldState }) => (
                                                    <div className="flex items-center">
                                                      <h3 className="text-sm text-gray-700 text-end">
                                                        {"開始年月日"}
                                                      </h3>
                                                      <TextField
                                                        {...field}
                                                        type={(ptAwardedSettings.pointLimitMap && ptAwardedSettings.pointLimitMap.dateFrom) ? "date" : "label"}
                                                        label={""}
                                                        color={"indigo"}
                                                        className={"flex items-center gap-x-3 h-10"}
                                                        selectClassName={"py-2 h-auto"}
                                                        style={{ 'width': '8.5rem', 'border-width': 0 }}
                                                        inputClassName={"bg-indigo-50 mb-1"}
                                                        value={(ptAwardedSettings.pointLimitMap && ptAwardedSettings.pointLimitMap.dateFrom) ? ptAwardedSettings.pointLimitMap.dateFrom : ""}
                                                        disabled
                                                      />
                                                    </div>
                                                  )}
                                                />
                                              </div>
                                              <div className="h-full pt-5 pb-5">
                                                <Controller
                                                  name={"pointLimitMap.dateTo"}
                                                  control={control}
                                                  render={({ field, fieldState }) => (
                                                    <div className="flex items-center">
                                                      <h3 className="text-sm text-gray-700 text-end">
                                                        {"終了年月日"}
                                                      </h3>
                                                      <TextField
                                                        {...field}
                                                        type={(ptAwardedSettings.pointLimitMap && ptAwardedSettings.pointLimitMap.dateTo) ? "date" : "label"}
                                                        label={""}
                                                        color={"indigo"}
                                                        className={"flex items-center gap-x-3 h-10"}
                                                        selectClassName={"py-2 h-auto"}
                                                        style={{ 'width': '8.5rem', 'border-width': 0 }}
                                                        inputClassName={"bg-indigo-50  mb-1"}
                                                        value={(ptAwardedSettings.pointLimitMap && ptAwardedSettings.pointLimitMap.dateTo) ? ptAwardedSettings.pointLimitMap.dateTo : ""}
                                                        disabled
                                                      />
                                                    </div>
                                                  )}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}
                                {ptAwardedSettings.typeId == PointTypeId.VOLUNTEER_REGIST || ptAwardedSettings.typeId == PointTypeId.GROUP_REGIST || ptAwardedSettings.typeId == PointTypeId.FIRST_LOGIN ? (
                                  <></>
                                ) : (
                                  <>
                                    <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 w-[47rem]">
                                      <div className="flex">
                                        <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                          <label className="block text-sm font-medium text-gray-700 bg-indigo-50">
                                            ボーナスポイント
                                          </label>
                                        </span>
                                      </div>
                                      <div className="pl-5 pt-2 pb-5 mt-4 text-sm">
                                        {getRadioName(selectFlgItems, ptAwardedSettings.bonusSettingFlg)}
                                        <div className="h-full mt-4">
                                          {/* {isLoad === true ? <Spiner /> :( */}
                                          <div className={"px-6 bg-indigo-50"}>
                                            <TableList
                                              header={false}
                                              footer={false}
                                              defaultSortKey={"sort"}
                                              sort={false}
                                              gridCol={bonusPointTableColumns}
                                              gridRow={bonusPointTableRowList}
                                            />
                                          </div>
                                          {/* )} */}
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={'flex mr-8 mb-2 mt-3 justify-end'}>
                    {pdfButton ?
                      <>
                        <div className="w-[150px] mr-2">
                          <Button
                            size={"sm"}
                            className={"ml-2 h-[48px] w-[150px]"}
                            // color={"violet"}
                            onClick={async () => {
                              if (await checkOption("point", dispatch)) {
                                pdfDownloadConfirmation();
                                }
                              }
                            }
                          >
                            二次元コードPDF出力
                          </Button>
                        </div>
                      </>
                      :
                      <></>
                    }
                    <Button
                      size={"md"}
                      className={"ml-2 h-[48px] w-[150px] pt-3"}
                      onClick={async () => {
                        if (await checkOption("point", dispatch)) {
                          toggleEditDialog();
                          }
                        }
                      }
                    >
                      &emsp;&emsp;編集&emsp;&emsp;
                    </Button>
                    <Button
                      color={"red"}
                      size={"md"}
                      className={"ml-2 h-[48px] w-[150px] pt-3"}
                      onClick={async () => {
                        if (await checkOption("point", dispatch)) {
                          toggleDeleteDialog();
                          }
                        }
                      }
                    >
                      &emsp;&emsp;削除&emsp;&emsp;
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <div>
          {isOpenEditDialog && (
            <PointAwardedEditModal
              children
              open={isOpenEditDialog}
              toggle={toggleEditDialog}
              closeOnClickOutside={false}
              settings={ptAwardedSettings}
              bonusSettings={ptBonusSettings}
              infoDataId={ptAwardedSettings.id}
              rangeStepsEditData={rangeStepsData}
            />
          )}
          <ModalAndAlmightyDialog
            toggle={toggleDialog}
            open={isOpenDialog}
            title={dialogTitle}
            children={dialogChildren}
            actions={dialogActions}
            height={"72"}
            closeOnClickOutside={false}
          />
          <DeleteConfirmationModal
            toggle={toggleDeleteDialog}
            open={isOpenDeleteDialog}
            children={""}
            closeOnClickOutside={false}
            handle={onClickDelete}
            title={ConstMessage.POINT_SETTING_DELETE_TITLE}
            body={ConstMessage.POINT_SETTING_DELETE_TEXT}
          />
        </div>
      </div>
    </>
  );
};